import { useCallback, useEffect, useState } from "react";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { GraduationCap, Loader2, RefreshCw, Search } from "lucide-react";
import { Input } from "../components/ui/input";

const pct = (v, nd = 1) => (v === null || v === undefined ? "—" : `${(v * 100).toFixed(nd)}%`);
const fmtAum = (v) => {
  if (!v) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  return `$${(v / 1e6).toFixed(0)}M`;
};
const LETTER_CLS = {
  A: "text-emerald-400 border-emerald-500/50",
  B: "text-sky-400 border-sky-500/50",
  C: "text-muted-foreground border-border",
  D: "text-amber-400 border-amber-500/50",
  F: "text-red-400 border-red-500/50",
};

const LetterBadge = ({ letter }) => (
  <span className={`px-1.5 py-0.5 rounded border font-mono font-bold text-[11px] ${LETTER_CLS[letter] || LETTER_CLS.C}`}>
    {letter || "—"}
  </span>
);

const Spark = ({ history }) => {
  if (!history || history.length < 2) return <span className="text-muted-foreground">—</span>;
  const vals = history.map((h) => h.grade);
  const min = Math.min(...vals), max = Math.max(...vals);
  const rng = max - min || 1;
  const pts = vals.map((v, i) =>
    `${(i / (vals.length - 1)) * 60},${16 - ((v - min) / rng) * 14}`).join(" ");
  const up = vals[vals.length - 1] >= vals[0];
  return (
    <svg width="60" height="18" className="inline-block">
      <polyline points={pts} fill="none" strokeWidth="1.2"
        stroke={up ? "rgb(52 211 153)" : "rgb(248 113 113)"} />
    </svg>
  );
};

export default function FundGradesPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [metaInfo, setMetaInfo] = useState(null);
  const [history, setHistory] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [fType, setFType] = useState("all");
  const [fStyle, setFStyle] = useState("all");
  const [fLetter, setFLetter] = useState("all");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    const get = async (p) => {
      const r = await fetch(`${API}/mf/${p}`, auth);
      return r.ok ? r.json() : null;
    };
    const [g, m, h] = await Promise.all([get("grades"), get("meta"), get("grades/history")]);
    if (h?.rows) {
      const by = {};
      h.rows.forEach((r) => { (by[r.fund] = by[r.fund] || []).push(r); });
      Object.values(by).forEach((a) => a.sort((x, y) => x.obs_date.localeCompare(y.obs_date)));
      setHistory(by);
    }
    setData(g); setMetaInfo(m); setLoading(false);
    return g;
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (token) load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const regrade = async () => {
    setBusy(true);
    toast.info("Recomputing grades — takes ~1 minute");
    await fetch(`${API}/mf/grades/refresh`, { ...auth, method: "POST" });
    const poll = setInterval(async () => {
      const g = await load();
      if (g) { clearInterval(poll); setBusy(false); toast.success("Grades updated"); }
    }, 10000);
    setTimeout(() => { clearInterval(poll); setBusy(false); }, 300000);
  };

  const openDetail = async (sym) => {
    setDetailLoading(true);
    setDetail({ symbol: sym });
    const r = await fetch(`${API}/mf/funds/${sym}`, auth);
    if (r.ok) setDetail(await r.json());
    setDetailLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="fund-grades-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const v = metaInfo?.validation;
  const allRows = data?.rows || [];
  const rows = allRows.filter((r) => r.graded !== false);
  const smallPeers = allRows.filter((r) => r.ungraded_reason === "small_cap")
    .sort((a, b) => (b.conv_exp || 0) - (a.conv_exp || 0));
  const indexLike = allRows.filter((r) => r.graded === false && r.ungraded_reason !== "small_cap")
    .sort((a, b) => (b.aum || 0) - (a.aum || 0));
  const q = search.trim().toLowerCase();
  const filtered = rows.filter((r) =>
    (fType === "all" || r.type === fType) &&
    (fStyle === "all" || r.style === fStyle) &&
    (fLetter === "all" || r.letter === fLetter) &&
    (!q || r.fund.toLowerCase().includes(q) || (r.name || "").toLowerCase().includes(q)));
  const shown = filtered.slice(0, 400);

  const selCls = "bg-background border border-border rounded px-2 py-1 text-xs text-foreground";

  return (
    <div className="space-y-4" data-testid="fund-grades-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold">Fund Grades — Active Mutual Funds & ETFs</h1>
          <p className="text-xs text-muted-foreground">
            Point-in-time N-PORT holdings graded on Kensys discriminative features
            · as of {data?.obs_date || "—"} · {rows.length} funds
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={regrade} disabled={busy}
                data-testid="fund-grades-refresh-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Regrade
        </Button>
      </div>

      {v && (
        <Card data-testid="fund-grades-validation-card">
          <CardContent className="py-3 space-y-1">
            <p className="text-[11px] text-muted-foreground italic border-l-2 border-sky-500/40 pl-2">
              Backtest ({v.window}): top-tercile beats bottom-tercile by{" "}
              <span className="text-emerald-400 font-semibold">
                +{v.results.peer_tercile_3m_sa.spread_pct_per_q}%/quarter style-adjusted
                (t={v.results.peer_tercile_3m_sa.t}, {v.results.peer_tercile_3m_sa.pct_pos_months}% of months positive)
              </span>
              ; +{v.results.peer_tercile_6m_sa.spread_pct_per_6m}%/6M (t={v.results.peer_tercile_6m_sa.t}, {v.results.peer_tercile_6m_sa.pct_pos_months}% positive).
              Per segment (6M): Large +{v.results.large_tercile_6m_sa.spread_pct_per_6m}% (t={v.results.large_tercile_6m_sa.t}),
              Mid +{v.results.mid_tercile_6m_sa.spread_pct_per_6m}% (t={v.results.mid_tercile_6m_sa.t}),
              Small {v.results.small_tercile_6m_sa.spread_pct_per_6m}% (t={v.results.small_tercile_6m_sa.t}) — no edge, hence unrated.
            </p>
            <p className="text-[11px] text-muted-foreground pl-2">
              {v.grade}. <span className="font-medium">active_share</span> = {v.definitions.active_share}
            </p>
            <p className="text-[10px] text-muted-foreground/70 pl-2">{v.note}</p>
          </CardContent>
        </Card>
      )}

      <Card data-testid="fund-grades-table-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
            <GraduationCap className="w-4 h-4 text-sky-400" />
            Leaderboard
            <span className="text-xs text-muted-foreground font-normal">
              {filtered.length} of {rows.length} funds{filtered.length > 400 ? " (showing top 400 — refine search)" : ""} · click a fund for holdings & grade history
            </span>
            <span className="flex items-center gap-2 ml-auto">
              <span className="relative">
                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="Search fund…" className="h-7 w-44 pl-6 text-xs"
                       data-testid="fund-grades-search" />
              </span>
              <select value={fType} onChange={(e) => setFType(e.target.value)}
                      className={selCls} data-testid="fund-grades-filter-type">
                <option value="all">All types</option>
                <option value="etf">ETF</option>
                <option value="mutual_fund">Mutual fund</option>
              </select>
              <select value={fStyle} onChange={(e) => setFStyle(e.target.value)}
                      className={selCls} data-testid="fund-grades-filter-style">
                <option value="all">All styles</option>
                <option value="growth">Growth</option>
                <option value="value">Value</option>
                <option value="blend">Blend</option>
              </select>
              <select value={fLetter} onChange={(e) => setFLetter(e.target.value)}
                      className={selCls} data-testid="fund-grades-filter-letter">
                <option value="all">All grades</option>
                {["A", "B", "C", "D", "F"].map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="max-h-[560px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-border text-muted-foreground sticky top-0 bg-background">
                <tr>
                  <th className="py-1 px-2 text-left">#</th>
                  <th className="py-1 px-2 text-left">Fund</th>
                  <th className="py-1 px-2 text-left">Type</th>
                  <th className="py-1 px-2 text-left">Style</th>
                  <th className="py-1 px-2 text-left">Segment</th>
                  <th className="py-1 px-2 text-right">AUM</th>
                  <th className="py-1 px-2 text-center">Grade</th>
                  <th className="py-1 px-2 text-right">Score</th>
                  <th className="py-1 px-2 text-center">Trend</th>
                  <th className="py-1 px-2 text-right">Converged exp</th>
                  <th className="py-1 px-2 text-right">Active share</th>
                  <th className="py-1 px-2 text-right">Strong share</th>
                  <th className="py-1 px-2 text-right">Quiet share</th>
                  <th className="py-1 px-2 text-right">Holdings age</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.fund}
                      className="border-b border-border/40 cursor-pointer hover:bg-muted/40"
                      onClick={() => openDetail(r.fund)}
                      data-testid={`fund-grade-row-${r.fund}`}>
                    <td className="py-1 px-2 font-mono text-muted-foreground">{r.rank}</td>
                    <td className="py-1 px-2">
                      <span className="font-semibold">{r.fund}</span>
                      <span className="text-muted-foreground ml-1 text-[10px]">{r.name}</span>
                    </td>
                    <td className="py-1 px-2 text-muted-foreground">{r.type === "etf" ? "ETF" : "MF"}</td>
                    <td className="py-1 px-2 text-muted-foreground">{r.style}</td>
                    <td className="py-1 px-2">
                      <span className={`px-1 py-0.5 rounded border text-[9px] font-mono uppercase ${r.segment === "large" ? "border-sky-500/40 text-sky-400" : "border-violet-500/40 text-violet-400"}`}>
                        {r.segment}
                      </span>
                    </td>
                    <td className="py-1 px-2 text-right font-mono text-muted-foreground">{fmtAum(r.aum)}</td>
                    <td className="py-1 px-2 text-center"><LetterBadge letter={r.letter} /></td>
                    <td className="py-1 px-2 text-right font-mono">{r.grade?.toFixed(2)}</td>
                    <td className="py-1 px-2 text-center"><Spark history={history[r.fund]} /></td>
                    <td className="py-1 px-2 text-right font-mono text-emerald-400">{pct(r.conv_exp)}</td>
                    <td className="py-1 px-2 text-right font-mono text-sky-400">{pct(r.active_share, 0)}</td>
                    <td className="py-1 px-2 text-right font-mono">{pct(r.strong_share)}</td>
                    <td className="py-1 px-2 text-right font-mono">{pct(r.quiet_share)}</td>
                    <td className="py-1 px-2 text-right font-mono text-muted-foreground">{r.staleness_d}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {smallPeers.length > 0 && (
        <Card data-testid="small-cap-funds-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <span className="px-1.5 py-0.5 rounded border border-violet-500/40 text-violet-400 font-mono text-[10px]">SMALL-CAP</span>
              Truly active, not letter-graded — {smallPeers.length} funds
              <span className="text-xs text-muted-foreground font-normal">
                converged names skew large-cap; the conviction edge is absent in small-caps, so grades would be fake precision
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="max-h-[260px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border text-muted-foreground sticky top-0 bg-background">
                  <tr>
                    <th className="py-1 px-2 text-left">Fund</th>
                    <th className="py-1 px-2 text-left">Type</th>
                    <th className="py-1 px-2 text-right">AUM</th>
                    <th className="py-1 px-2 text-right">Active share</th>
                    <th className="py-1 px-2 text-right">Converged exp</th>
                    <th className="py-1 px-2 text-right">Holdings age</th>
                  </tr>
                </thead>
                <tbody>
                  {smallPeers.map((r) => (
                    <tr key={r.fund}
                        className="border-b border-border/40 cursor-pointer hover:bg-muted/40 text-muted-foreground"
                        onClick={() => openDetail(r.fund)}
                        data-testid={`small-cap-row-${r.fund}`}>
                      <td className="py-1 px-2">
                        <span className="font-semibold text-foreground/70">{r.fund}</span>
                        <span className="ml-1 text-[10px]">{r.name}</span>
                      </td>
                      <td className="py-1 px-2">{r.type === "etf" ? "ETF" : "MF"}</td>
                      <td className="py-1 px-2 text-right font-mono">{fmtAum(r.aum)}</td>
                      <td className="py-1 px-2 text-right font-mono">{pct(r.active_share, 0)}</td>
                      <td className="py-1 px-2 text-right font-mono">{pct(r.conv_exp)}</td>
                      <td className="py-1 px-2 text-right font-mono">{r.staleness_d}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {indexLike.length > 0 && (
        <Card data-testid="index-like-funds-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <span className="px-1.5 py-0.5 rounded border border-amber-500/40 text-amber-400 font-mono text-[10px]">INDEX-LIKE</span>
              Not graded — {indexLike.length} funds
              <span className="text-xs text-muted-foreground font-normal">
                rolling 3-month active share &lt; 80% vs its best-fit benchmark (large / mid / small) — the book largely mirrors an index, so the conviction grade isn't applied
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="max-h-[300px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border text-muted-foreground sticky top-0 bg-background">
                  <tr>
                    <th className="py-1 px-2 text-left">Fund</th>
                    <th className="py-1 px-2 text-left">Type</th>
                    <th className="py-1 px-2 text-left">Style</th>
                    <th className="py-1 px-2 text-right">AUM</th>
                    <th className="py-1 px-2 text-right">Active share</th>
                    <th className="py-1 px-2 text-right">Converged exp</th>
                    <th className="py-1 px-2 text-right">Holdings age</th>
                  </tr>
                </thead>
                <tbody>
                  {indexLike.map((r) => (
                    <tr key={r.fund}
                        className="border-b border-border/40 cursor-pointer hover:bg-muted/40 text-muted-foreground"
                        onClick={() => openDetail(r.fund)}
                        data-testid={`index-like-row-${r.fund}`}>
                      <td className="py-1 px-2">
                        <span className="font-semibold text-foreground/70">{r.fund}</span>
                        <span className="ml-1 text-[10px]">{r.name}</span>
                      </td>
                      <td className="py-1 px-2">{r.type === "etf" ? "ETF" : "MF"}</td>
                      <td className="py-1 px-2">{r.style}</td>
                      <td className="py-1 px-2 text-right font-mono">{fmtAum(r.aum)}</td>
                      <td className="py-1 px-2 text-right font-mono text-amber-400">{pct(r.active_share, 0)}</td>
                      <td className="py-1 px-2 text-right font-mono">{pct(r.conv_exp)}</td>
                      <td className="py-1 px-2 text-right font-mono">{r.staleness_d}d</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl" data-testid="fund-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {detail?.registry?.name || detail?.symbol}
              <span className="text-muted-foreground font-normal ml-2 text-xs">
                {detail?.latest_period ? `holdings as of ${detail.latest_period}` : ""}
              </span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Latest disclosed holdings and Kensys grade history
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : detail?.registry ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <p className="font-semibold mb-1">Top holdings</p>
                <div className="max-h-[320px] overflow-y-auto">
                  <table className="w-full">
                    <tbody>
                      {(detail.holdings || []).slice(0, 20).map((h, i) => (
                        <tr key={i} className="border-b border-border/30">
                          <td className="py-0.5 pr-2 font-semibold">{h.symbol || "—"}</td>
                          <td className="py-0.5 pr-2 text-muted-foreground truncate max-w-[180px]">{h.holding_name}</td>
                          <td className="py-0.5 text-right font-mono">{h.pct_val?.toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <p className="font-semibold mb-1">Grade history</p>
                <div className="max-h-[320px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="text-muted-foreground">
                      <tr><th className="text-left py-0.5">Month</th>
                          <th className="text-center py-0.5">Grade</th>
                          <th className="text-right py-0.5">Score</th></tr>
                    </thead>
                    <tbody>
                      {(detail.grade_history || []).slice().reverse().map((g) => (
                        <tr key={g.obs_date} className="border-b border-border/30">
                          <td className="py-0.5 font-mono">{g.obs_date}</td>
                          <td className="py-0.5 text-center"><LetterBadge letter={g.letter} /></td>
                          <td className="py-0.5 text-right font-mono">{g.grade?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
